import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { collection, query, where, orderBy, limit, startAfter, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import {
  estadoInicial, aplicarCambios, anexarPagina, listaFusionada, totalDeChats,
} from '../componentes/inbox/fusionDeChats';

/**
 * LA LISTA DE LA BANDEJA, VIVA — y en el proveedor, no en la pantalla.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * EL REPORTE QUE LO ORIGINA (24-sep-2026)
 * ───────────────────────────────────────────────────────────────────────────────
 * «La bandeja no se actualiza: hay que salir y entrar para ver mensajes nuevos.» La
 * causa estaba medida: `Chats` no tenía **ni un `onSnapshot`**, sólo tres `getDocs`. Era
 * una foto del momento de entrar.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * ⚠️ EL OYENTE VA EN EL PROVEEDOR, Y AHÍ ESTÁ TODO EL AHORRO
 * ───────────────────────────────────────────────────────────────────────────────
 * Montado dentro de `Chats` se desengancharía al abrir un chat y **se reengancharía al
 * volver: otra vez la página entera**. El ahorro desaparecería completo.
 *
 * En el proveedor —que sobrevive el viaje al detalle— la lista ya está fresca al volver.
 * No hay que releer nada.
 *
 * (Aquí decía además que «la insignia de no leído se corrige sola cuando `ChatDetalle`
 * escribe `mensajesNoLeidos: 0`». Eso dejó de ocurrir el 24-sep-2026: esa escritura se
 * retiró y la insignia pasó a derivarse de `ultimoRemitente`. El oyente sigue siendo lo
 * que mantiene la lista fresca; simplemente ya no hay ningún contador que apagar.)
 *
 * Medido sobre la organización grande (296 chats, 76 mensajes/día en 15 chats):
 *
 *     hoy      turno con 20 chats abiertos ≈ 4 740 lecturas (237 por cada ida y vuelta)
 *     ahora    237 al montar + ~76 en todo el día ≈ 313
 *
 * Quince veces menos, y además la lista se mueve sola.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * EL OYENTE CUBRE SÓLO LA PÁGINA 1, TAMBIÉN CON FILTRO ACTIVO
 * ───────────────────────────────────────────────────────────────────────────────
 * `limit(200)` siempre. Ampliarlo cuando hay filtro obligaría a **reenganchar el oyente
 * cada vez que se toca un filtro**, y cada reenganche cuesta la página entera: se pagaría
 * el ahorro por una diferencia marginal.
 *
 * Los que quedan congelados sólo están rancios hasta que alguno recibe un mensaje — y
 * entonces entra solo al rango vivo. La coherencia la sostiene `fusionDeChats`, que es
 * pura y tiene su propia guarda.
 */

/** La página del oyente. Es el mismo 200 que ya usaba la paginación. */
const TAMANO_PAGINA = 200;

/**
 * CON CUENTA ELEGIDA, LA CONSULTA FILTRA (commit 10 de multicuenta, 23-oct-2026): `where('accountId','==',x)` +
 * `orderBy('actualizadoEn','desc')` (índice `chats (accountId ASC, actualizadoEn DESC)` en `firestore.indexes.json`),
 * en el oyente y en la paginación. No es un filtro en memoria sobre páginas: con 415 chats de un número y 30 de otro,
 * agotar páginas para encontrar los del segundo habría costado todas. El oyente se reengancha SÓLO al cambiar de
 * cuenta (una página por cambio), no al tocar canal ni estado. Requiere el backfill de `accountId` (commit 11):
 * sin él, los chats legados sin el campo no salen al filtrar.
 */
export function useDatosDeBandeja({ user, context, orgLoading, accountId = null }) {
  const [estado, setEstado] = useState(estadoInicial);
  const [hayMas, setHayMas] = useState(true);
  const [cargando, setCargando] = useState(false);
  /** El oyente falló: la lista puede estar desactualizada y hay que DECIRLO. */
  const [oyenteCaido, setOyenteCaido] = useState(false);

  const cursorRef = useRef(null);
  const cargaEnCurso = useRef(null);
  // Cada reenganche del oyente (cambio de tenant o de cuenta) es una GENERACIÓN nueva: una página
  // en vuelo de la anterior no puede anexarse ni mover el cursor (revisión del commit 10, 🟠5).
  const generacionRef = useRef(0);
  // …y ADEMÁS como estado, para quien agota páginas (`Chats`): sus efectos corren ANTES que los
  // de este proveedor en el mismo commit, así que su primera llamada tras cambiar de cuenta nace
  // con la generación vieja y se descarta; al subir `generacion` como estado, su efecto vuelve a
  // correr ya con la nueva (2.ª revisión del commit 10, 🟠).
  const [generacion, setGeneracion] = useState(0);
  const hayMasRef = useRef(true);
  /** ¿Llegó a haber alguna lista? Decide si un fallo puede dejar la pantalla vacía. */
  const huboDatos = useRef(false);

  const rutaDeChats = useCallback(() => {
    if (context?.mode === 'organization' && context?.orgId) {
      return collection(db, 'organizations', context.orgId, 'chats');
    }
    if (!user?.uid) return null;
    return collection(db, 'clientes', user.uid, 'chats');
  }, [context?.mode, context?.orgId, user?.uid]);

  // ── EL OYENTE ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user || orgLoading) return undefined;
    const col = rutaDeChats();
    if (!col) return undefined;

    // Al cambiar de organización se empieza de cero: mezclar chats de dos tenants sería
    // una fuga de datos, no un fallo de pintado.
    generacionRef.current += 1;
    setGeneracion(generacionRef.current);
    cargaEnCurso.current = null;
    setEstado(estadoInicial());
    cursorRef.current = null;
    hayMasRef.current = true;
    setHayMas(true);
    huboDatos.current = false;
    setOyenteCaido(false);

    const q = query(col, ...(accountId ? [where('accountId', '==', accountId)] : []), orderBy('actualizadoEn', 'desc'), limit(TAMANO_PAGINA));

    const parar = onSnapshot(
      q,
      (snap) => {
        huboDatos.current = true;
        setOyenteCaido(false);
        // `docChanges()` y no `snap.docs`: sólo lo que cambió. Es lo que hace que un
        // mensaje nuevo cueste 1 lectura y no 200.
        const cambios = snap.docChanges().map((c) => ({
          type: c.type, id: c.doc.id, data: c.doc.data(),
        }));
        setEstado((prev) => aplicarCambios(prev, cambios));
        // El cursor sale del ÚLTIMO documento vivo: es desde donde sigue la página 2.
        const ultimo = snap.docs[snap.docs.length - 1] || null;
        cursorRef.current = ultimo;
        if (snap.size < TAMANO_PAGINA) { hayMasRef.current = false; setHayMas(false); }
      },
      async (error) => {
        // ⚠️ NO SE VACÍA LA LISTA. Lo último conocido se queda en pantalla y se enciende
        // el aviso. El caso real es el de permisos —cambiar de organización, perder un
        // rol—: quedarse con la lista anterior SIN decir nada sería peor que vaciarse.
        console.error('[bandeja] el oyente falló:', error?.message || error);
        setOyenteCaido(true);
        // Si falló en el PRIMER enganche no hay nada que conservar: se cae a una lectura
        // de una vez para que la bandeja no salga en blanco.
        if (!huboDatos.current) {
          try {
            const snap = await getDocs(q);
            setEstado((prev) => anexarPagina(prev, snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
            cursorRef.current = snap.docs[snap.docs.length - 1] || null;
            if (snap.size < TAMANO_PAGINA) { hayMasRef.current = false; setHayMas(false); }
          } catch (e) {
            console.error('[bandeja] tampoco se pudo leer de una vez:', e?.message || e);
          }
        }
      }
    );

    return () => parar();
  }, [user, orgLoading, rutaDeChats, accountId]);

  // ── LAS PÁGINAS SIGUIENTES ─────────────────────────────────────────────────────
  /**
   * Trae la página siguiente y la anexa a la cola congelada.
   *
   * ⚠️ El cursor es SEGURO con el oyente activo, y no por suerte: `startAfter` recibe un
   * DocumentSnapshot y Firestore ordena POR VALORES, no por índice. Un chat que sube deja
   * de estar «después del cursor» y pasa a estar cubierto por el oyente — la página
   * devuelve uno menos, sin saltarse a nadie. Y bajar es imposible: `actualizadoEn` es un
   * `serverTimestamp` y sólo crece.
   *
   * Lo que sí pasa es que la página puede traer un chat que el oyente YA tiene fresco;
   * `anexarPagina` lo descarta. La copia viva gana en los dos sentidos.
   */
  const cargarPagina = useCallback(() => {
    if (!user || orgLoading || !hayMasRef.current) return Promise.resolve(0);
    if (cargaEnCurso.current) return cargaEnCurso.current;
    const col = rutaDeChats();
    if (!col) return Promise.resolve(0);

    const generacion = generacionRef.current;
    const promesa = (async () => {
      setCargando(true);
      try {
        const partes = [...(accountId ? [where('accountId', '==', accountId)] : []), orderBy('actualizadoEn', 'desc')];
        if (cursorRef.current) partes.push(startAfter(cursorRef.current));
        partes.push(limit(TAMANO_PAGINA));
        const snap = await getDocs(query(col, ...partes));
        // La cuenta cambió mientras cargaba: se descarta SIN tocar estado ni cursor. `null`, no `0`:
        // «0» significa «no hay más» y pararía el bucle que agota páginas.
        if (generacion !== generacionRef.current) return null;

        hayMasRef.current = snap.size === TAMANO_PAGINA;
        setHayMas(hayMasRef.current);
        if (snap.empty) return 0;

        cursorRef.current = snap.docs[snap.docs.length - 1];
        setEstado((prev) => anexarPagina(prev, snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
        return snap.size;
      } catch (err) {
        console.error('[bandeja] error cargando una página:', err?.message || err);
        // Se corta la paginación: insistir repite el error, y el IntersectionObserver lo
        // haría en bucle mientras el centinela esté a la vista.
        hayMasRef.current = false;
        setHayMas(false);
        return 0;
      } finally {
        // Una carga descartada no apaga el «cargando» de la generación nueva.
        if (generacion === generacionRef.current) setCargando(false);
      }
    })();

    cargaEnCurso.current = promesa;
    promesa.finally(() => { if (cargaEnCurso.current === promesa) cargaEnCurso.current = null; });
    return promesa;
  }, [user, orgLoading, rutaDeChats, accountId]);

  const chats = useMemo(() => listaFusionada(estado), [estado]);
  const total = useMemo(() => totalDeChats(estado), [estado]);

  return { chats, total, hayMas, cargando, oyenteCaido, cargarPagina, generacion };
}

export { TAMANO_PAGINA };
